import React, { useState } from 'react';

// Ľavý panel dashboardu: pohľady na doklady s počítadlami, odkazy na moduly
// a kompaktný mesačný prehľad. Pohľad = ?view= na GET /documents.
export type DashView = 'queue' | 'review' | 'mine' | 'passed' | 'approved' | 'rejected';
export type DashCounts = Partial<Record<DashView, number>>;

export interface SidebarLink { label: string; icon: string; href?: string; onClick?: () => void }

export interface SidebarMonth {
  month: string;
  label: string;
  total: number;
  waiting: { level: number; count: number }[];
  approved: number;
  sums: string[];
}

interface Props {
  view: DashView;
  counts: DashCounts;
  onView: (v: DashView) => void;
  queueLabel: string;
  links: SidebarLink[];
  months: SidebarMonth[];
}

const Item: React.FC<{ icon: string; label: string; count?: number; active: boolean; alert?: boolean; onClick: () => void }> =
  ({ icon, label, count, active, alert, onClick }) => (
    <button type="button" className={`dash-nav${active ? ' is-active' : ''}`} onClick={onClick}>
      <span className="dash-nav-label"><span aria-hidden>{icon}</span>{label}</span>
      {count !== undefined && (
        <span className={`dash-nav-count${alert && count > 0 && !active ? ' is-alert' : ''}`}>{count}</span>
      )}
    </button>
  );

export default function DashboardSidebar({ view, counts, onView, queueLabel, links, months }: Props) {
  const [allMonths, setAllMonths] = useState(false);
  const shown = allMonths ? months : months.slice(0, 1);
  return (
    <aside className="dash-side">
      <div className="dash-side-title">Na mne</div>
      <Item icon="⏳" label={queueLabel} count={counts.queue} active={view === 'queue'} onClick={() => onView('queue')} />
      <Item icon="🔍" label="Kontrola" count={counts.review} alert active={view === 'review'} onClick={() => onView('review')} />

      <div className="dash-side-title">Sledujem</div>
      <Item icon="📤" label="Moje odoslané" count={counts.mine} active={view === 'mine'} onClick={() => onView('mine')} />
      <Item icon="➡️" label="Prešlo cezo mňa" count={counts.passed} active={view === 'passed'} onClick={() => onView('passed')} />
      <Item icon="✅" label="Schválené" count={counts.approved} active={view === 'approved'} onClick={() => onView('approved')} />
      <Item icon="↩️" label="Zamietnuté a vrátené" count={counts.rejected} active={view === 'rejected'} onClick={() => onView('rejected')} />

      {links.length > 0 && (
        <div className="dash-workspace">
          <div className="dash-side-title">Moduly</div>
          {links.map(l => l.href ? (
            <a key={l.label} className="dash-nav" href={l.href}>
              <span className="dash-nav-label"><span aria-hidden>{l.icon}</span>{l.label}</span>
            </a>
          ) : (
            <button key={l.label} type="button" className="dash-nav" onClick={l.onClick}>
              <span className="dash-nav-label"><span aria-hidden>{l.icon}</span>{l.label}</span>
            </button>
          ))}
        </div>
      )}

      {shown.map(m => (
        <div key={m.month} className="dash-month">
          <div className="dash-month-head"><span>{m.label}</span></div>
          <div className="dash-month-row"><span>Prijatých</span><strong>{m.total}</strong></div>
          {m.waiting.map(w => (
            <div key={w.level} className="dash-month-row"><span>Čaká na L{w.level}</span><strong>{w.count}</strong></div>
          ))}
          <div className="dash-month-row"><span>Schválených</span><strong>{m.approved}</strong></div>
          <div className="dash-month-row"><span>Suma bez DPH</span><strong style={{ textAlign: 'right' }}>{m.sums.map(s => <div key={s}>{s}</div>)}</strong></div>
        </div>
      ))}
      {months.length > 1 && (
        <div className="dash-month" style={{ border: 0, background: 'transparent', padding: '0 0.5rem', margin: '0.25rem 0 0' }}>
          <button type="button" className="dash-month-more" onClick={() => setAllMonths(v => !v)}>
            {allMonths ? 'Skryť staršie mesiace' : `Staršie mesiace (${months.length - 1})`}
          </button>
        </div>
      )}
    </aside>
  );
}
